<template>
  <section class="card op-log-page">
    <h2>操作日志</h2>
    <p class="tip">记录后台写操作（新增、修改、删除、审核等）。点击「详情」可查看请求方式、地址与参数。</p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="filters.operatorKeyword"
          class="flt-input flt-input--wide"
          placeholder="操作人员（姓名或账号）"
          aria-label="操作人员"
          @keyup.enter="onSearch"
        />
      </div>
      <div class="flt">
        <input
          v-model.trim="filters.actionKeyword"
          class="flt-input flt-input--wide"
          placeholder="操作类型（如：会员、审核）"
          aria-label="操作类型"
          @keyup.enter="onSearch"
        />
      </div>
      <div class="flt flt--range">
        <div class="range-row">
          <input
            v-model="filters.startAt"
            class="flt-input flt-input--time"
            type="datetime-local"
            aria-label="开始时间"
          />
          <span class="range-sep">~</span>
          <input
            v-model="filters.endAt"
            class="flt-input flt-input--time"
            type="datetime-local"
            aria-label="结束时间"
          />
        </div>
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" :disabled="loading" @click="onSearch">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" :disabled="loading" @click="reset">重置</button>
      </div>
    </div>

    <p v-if="loading" class="muted empty-hint">加载中…</p>
    <template v-else>
      <table class="op-log-table">
        <thead>
          <tr>
            <th class="col-seq">序号</th>
            <th>系统模块</th>
            <th>操作类型</th>
            <th>操作人员</th>
            <th>主机</th>
            <th>状态</th>
            <th>操作时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in pageRows" :key="row.id">
            <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
            <td>{{ labelTargetType(row.targetType) }}</td>
            <td>{{ labelAction(row.action) }}</td>
            <td>{{ operatorName(row.adminUserName) }}</td>
            <td>{{ logClientIp(rowDetailJson(row)) }}</td>
            <td><span class="status-pill status-pill--ok">成功</span></td>
            <td>{{ formatDateTime(row.createdAt) }}</td>
            <td>
              <button type="button" class="link-detail" @click="openDetail(row)">详情</button>
            </td>
          </tr>
          <tr v-if="!rows.length && !errorMsg">
            <td colspan="8" class="table-empty muted">暂无日志</td>
          </tr>
        </tbody>
      </table>

      <ListPager
        v-model:page="page"
        v-model:page-size="pageSize"
        :total="rows.length"
        :tip="rows.length >= fetchLimit ? `已加载最近匹配的 ${fetchLimit} 条，请缩小时间范围或使用筛选条件。` : ''"
      />
    </template>

    <div v-if="detailRow" class="mask mask--scroll" @click.self="closeDetail">
      <div class="dialog dialog--shell dialog--md dlg-op-log" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">操作详情</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDetail">×</button>
        </div>
        <div class="dialog-body">
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-k">操作模块</span>
              <span class="meta-v">{{ logModulePathLabel(detailRow) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-k">请求方式</span>
              <span class="meta-v">{{ logHttpMethod(detailJsonOf(detailRow)) }}</span>
            </div>
            <div class="meta-item meta-item--full">
              <span class="meta-k">登录信息</span>
              <span class="meta-v">{{ logLoginInfo(detailRow) }}</span>
            </div>
            <div class="meta-item meta-item--full">
              <span class="meta-k">操作方法</span>
              <span class="meta-v meta-v--mono">{{ logHandlerMethod(detailJsonOf(detailRow)) }}</span>
            </div>
            <div class="meta-item meta-item--full">
              <span class="meta-k">请求地址</span>
              <span class="meta-v meta-v--mono">{{ logRequestPath(detailJsonOf(detailRow)) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-k">状态</span>
              <span class="meta-v"><span class="status-pill status-pill--ok">成功</span></span>
            </div>
          </div>
          <p v-if="detailLegacyHint" class="legacy-hint">{{ detailLegacyHint }}</p>
          <div class="params-block">
            <div class="params-title">参数</div>
            <pre class="params-pre">{{ logParamsPretty(detailJsonOf(detailRow), detailRow) }}</pre>
          </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeDetail">关闭</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getAdminOperationLogs } from '@/api'
import ListPager from '@/components/ListPager.vue'
import {
  formatTargetId,
  labelAction,
  labelTargetType,
  logClientIp,
  logHandlerMethod,
  logHttpMethod,
  logLoginInfo,
  logModulePathLabel,
  logParamsPretty,
  logRequestPath,
  rowDetailJson,
} from '@/utils/operationLogLabels'
import { clampPage, pageRowOffset, slicePageRows, totalPagesFor } from '@/utils/listPager'

const FETCH_LIMIT = 500

const initFilters = () => ({
  operatorKeyword: '',
  actionKeyword: '',
  startAt: '',
  endAt: '',
})

export default {
  name: 'OperationLogView',
  components: { ListPager },
  data() {
    return {
      rows: [],
      errorMsg: '',
      loading: false,
      filters: initFilters(),
      page: 1,
      pageSize: 10,
      fetchLimit: FETCH_LIMIT,
      detailRow: null,
    }
  },
  computed: {
    totalPages() {
      return totalPagesFor(this.rows.length, this.pageSize)
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pageRows() {
      return slicePageRows(this.rows, this.page, this.pageSize)
    },
    detailLegacyHint() {
      if (!this.detailRow) return ''
      const d = rowDetailJson(this.detailRow)
      if (!d) return '该条为较早日志，未记录请求方式/地址/方法；请重新操作后查看完整详情。'
      const parsed = logHttpMethod(d)
      const path = logRequestPath(d)
      if (parsed === '—' && path === '—') {
        return '该条日志缺少接口详情（可能为旧数据或被截断）。'
      }
      return ''
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    labelAction,
    labelTargetType,
    formatTargetId,
    logClientIp,
    logHttpMethod,
    logHandlerMethod,
    logRequestPath,
    logParamsPretty,
    logModulePathLabel,
    logLoginInfo,
    rowDetailJson,
    detailJsonOf(row) {
      return rowDetailJson(row)
    },
    operatorName(name) {
      const s = String(name || '').trim()
      if (!s || s === '-') return '—'
      const idx = s.indexOf('(')
      return idx > 0 ? s.slice(0, idx) : s
    },
    buildQueryParams() {
      const f = this.filters
      const params = { limit: FETCH_LIMIT }
      if (f.operatorKeyword) params.operatorKeyword = f.operatorKeyword
      if (f.actionKeyword) params.keyword = f.actionKeyword
      if (f.startAt) params.startAt = this.toApiDateTime(f.startAt)
      if (f.endAt) params.endAt = this.toApiDateTime(f.endAt)
      return params
    },
    toApiDateTime(local) {
      const s = String(local || '').trim()
      if (!s) return ''
      if (s.length === 16) return `${s}:00`
      return s
    },
    async load() {
      this.loading = true
      this.errorMsg = ''
      try {
        this.rows = (await getAdminOperationLogs(this.buildQueryParams())) || []
        this.page = clampPage(this.page, this.rows.length, this.pageSize)
      } catch (e) {
        this.errorMsg = e.message || '读取日志失败'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.page = 1
      this.load()
    },
    reset() {
      this.filters = initFilters()
      this.page = 1
      this.load()
    },
    openDetail(row) {
      this.detailRow = row
    },
    closeDetail() {
      this.detailRow = null
    },
    formatDateTime(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.length >= 19 ? s.slice(0, 19) : s
    },
  },
}
</script>

<style scoped>
.op-log-page .flt-input--wide {
  width: 220px;
  min-width: 220px;
}
.flt--range {
  min-width: 400px;
}
.range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.range-sep {
  color: #98a2b3;
  font-size: 13px;
}
.flt-input--time {
  width: 190px;
  min-width: 190px;
  flex: 0 0 190px;
}
.op-log-table th,
.op-log-table td {
  text-align: center;
  vertical-align: middle;
}
.col-seq {
  width: 56px;
}
.table-empty {
  text-align: center;
  padding: 32px 12px !important;
  color: #98a2b3;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid #d0d5dd;
  background: #f9fafb;
  color: #344054;
}
.status-pill--ok {
  border-color: #abefc6;
  background: #ecfdf3;
  color: #067647;
}
.link-detail {
  border: none;
  background: none;
  color: #2f6af6;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
}
.link-detail:hover {
  text-decoration: underline;
}
.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
  margin-bottom: 16px;
}
.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.meta-item--full {
  grid-column: 1 / -1;
}
.meta-k {
  font-size: 12px;
  color: #667085;
}
.meta-v {
  font-size: 14px;
  color: #101828;
  word-break: break-all;
}
.meta-v--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
}
.params-block {
  border-top: 1px solid #eaecf0;
  padding-top: 12px;
}
.params-title {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}
.params-pre {
  margin: 0;
  padding: 12px 14px;
  max-height: 320px;
  overflow: auto;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: #344054;
  white-space: pre-wrap;
  word-break: break-all;
}
.legacy-hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
  border-radius: 8px;
  padding: 8px 10px;
}
</style>
